// src/oura/components/RescanSheet.tsx
//
// Rescan with the same 50 (JOURNEY-SPEC v1.1 §2.2, F2): a small drawer that
// asks the business's frozen set again through POST /businesses/{id}/rescan.
// Nothing about the 50 changes here; changing them is Edit the questions.
//
// Staff only. The paid start sits behind the same confirm row every Start
// step uses: "This starts a paid scan. Start it?" with Start and Cancel.

import { useEffect, useState } from "react";
import { Drawer, DrawerBlock, DrawerFoot, DrawerHead, PillButton, TYPE } from "@oura/ui";
import { rehydrateScanJobs } from "../aeo";
import { rescanBusiness } from "@service/aeo";
import { FIRST_CHECK_MINUTES, dayMonth } from "../onboarding/derive";
import { cn } from "../lib/cn";

export interface RescanSheetProps {
    open: boolean;
    businessId: string;
    businessName: string;
    domain: string;
    /** The set the last scan asked ("set v3"). Null: not known, the sheet says "the same 50". */
    setVersion: number | null;
    /** What the last scan cost, in US dollars. Null: not recorded, the sheet names no figure. */
    costUsd: number | null;
    /** The next monthly check. Null: monthly checks are off for this business. */
    nextScanAt: string | null;
    onClose: () => void;
    /** The scan is queued: the caller says so and the scan toast takes it from there. */
    onStarted: () => void;
}

const MINUTES = `About ${FIRST_CHECK_MINUTES} minutes`;

export function RescanSheet({ open, businessId, businessName, domain, setVersion, costUsd, nextScanAt, onClose, onStarted }: RescanSheetProps) {
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) return;
        setConfirming(false);
        setBusy(false);
        setError(null);
    }, [open]);

    const start = async () => {
        if (busy) return;
        setBusy(true);
        setError(null);
        const res = await rescanBusiness(businessId, { domain, businessName });
        setBusy(false);
        if (res.ok || res.kind === "running") {
            // The scan toast and the business's Scanning tag read the job store.
            void rehydrateScanJobs();
        }
        if (res.ok) {
            onStarted();
            return;
        }
        setConfirming(false);
        setError(res.message);
    };

    const asks = setVersion !== null ? `set v${setVersion}` : "the same 50";
    const cost = costUsd !== null ? `, about US$${costUsd.toFixed(2)}` : "";
    const monthly = nextScanAt ? dayMonth(nextScanAt) : "";

    return (
        <Drawer open={open} onClose={busy ? () => {} : onClose} width={480}>
            <DrawerHead title="Rescan with the same 50" status={{ label: businessName }} />
            <DrawerBlock>
                <p className={cn(TYPE.tb, "m-0 text-gray-700")}>
                    ChatGPT app, Gemini and Google AI ask {asks} again. {MINUTES}{cost}.
                </p>
                {monthly && (
                    <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>Monthly checks run by themselves on {monthly}. This adds one now.</p>
                )}
            </DrawerBlock>
            <DrawerFoot
                message={error ? { tone: "error", text: error } : undefined}
                first={(
                    <PillButton variant="primary" onClick={() => setConfirming(true)}>
                        Rescan
                    </PillButton>
                )}
                second={<PillButton variant="text" onClick={onClose}>Cancel</PillButton>}
                confirm={confirming ? {
                    text: "This starts a paid scan. Start it?",
                    verb: "Start",
                    onConfirm: () => void start(),
                    onCancel: () => setConfirming(false),
                    busy,
                } : undefined}
            />
        </Drawer>
    );
}
